import { useSelector } from '@xstate/react'
import { motion, MotionStyle } from 'framer-motion'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import { useMedia } from 'react-use'
import styled, { css } from 'styled-components'
import { media, tabletMaxWidth } from 'utils/media'
import { useABTest } from 'utils/useABTest'
import { Button } from 'lib/Button/Button'
import { Hamburger } from 'lib/Hamburger'
import { Anchor, Link } from 'lib/Link'
import { HorizontalLogo } from 'lib/Logo'
import { MaxWidthContainer } from 'lib/Section'
import { textSize, textWeight } from 'lib/Text'
import { Nav } from 'lib/layout/Nav'
import { GlobalSearch } from 'lib/search/GlobalSearch'
import { useUiContext } from 'modules/UiContext'
import { useAuthentication } from 'modules/auth/useAuthentication'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { AlgoliaSearchProvider } from 'modules/brands/list/components/controllers/AlgoliaSearchContext'
import { UserProfile } from './UserProfile'

export interface HeaderTheme {
  foreground: string
  background: string
  hideExtras?: boolean
  borderThickness?: string
  animateTransition?: boolean
}

const defaultTheme: HeaderTheme = {
  foreground: 'black',
  background: 'white',
  hideExtras: false,
  borderThickness: '1px',
  animateTransition: false
}

const HeaderContent = styled(MaxWidthContainer)`
  height: var(--header-height);
  padding: 0 var(--wide-grid-spacing);
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  position: relative;
  max-width: var(--max-column-width);
  margin: 0 auto;
  z-index: 100;

  ${media.desktopBoundsDown} {
    padding: 0;
  }

  ${media.phoneDown} {
    height: 60px;
  }
`

const HeaderLinks = styled(motion.nav)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  margin-left: 24px;

  ${media.phoneDown} {
    margin-left: 0;
    width: auto;
  }
`

const Logo = styled(HorizontalLogo)<{ menuOpen: boolean; theme: HeaderTheme }>`
  width: 240px;
  box-sizing: border-box;
  ${media.desktopBoundsDown} {
    margin-left: var(--wide-grid-spacing);
    margin-right: 18px;
  }

  ${media.phoneDown} {
    width: auto;
    max-width: 164px;
  }

  path {
    transition: fill 300ms cubic-bezier(0.25, 0.1, 0.25, 1);
    fill: ${(props) => props.theme.foreground};

    ${(props) =>
      props.menuOpen &&
      css`
        fill: white;
      `}
  }
`

const LogoAnchor = styled(Anchor)`
  flex-grow: 0;
  border-bottom: none;
  line-height: 0;
  z-index: 1001;

  ${media.phoneDown} {
    flex-grow: 1;
  }

  &:hover {
    border-bottom: none;
  }
`

const PrimaryLink = styled(Link)`
  ${textWeight('semi')}
  ${textSize('ps')}
  border-bottom-color: transparent;
  border-bottom: 2px solid transparent;
`

const PrimaryLinks = styled(motion.div)<{ $isSignedIn?: boolean }>`
  align-items: center;
  display: flex;
  gap: var(--item-spacing);
  margin-right: var(--item-spacing);

  ${media.tabletDown} {
    display: none;
  }

  ${(props) =>
    !props.$isSignedIn &&
    css`
      @media (max-width: 980px) {
        ${PrimaryLink} {
          display: none;
        }
      }
    `}
`

const SignInButton = styled(Button)`
  white-space: nowrap;
  margin-right: 12px;
  ${media.phoneDown} {
    display: none;
  }
`

const SignUpButton = styled(Button)`
  && {
    margin-right: var(--item-spacing);
    white-space: nowrap;
  }
`

const SignupLinks = styled.div`
  border-left: 1px solid var(--black-20);
  padding-left: var(--item-spacing);
  display: flex;
  align-items: center;
  ${media.tabletDown} {
    border-left: 0;
  }
  ${media.smallPhoneDown} {
    display: none;
  }
`

const Container = styled(motion.div)<{ $border: boolean; theme: HeaderTheme }>`
  --item-spacing: 24px;
  background: ${(props) => props.theme.background ?? 'white'};
  position: sticky;
  top: 0px;
  z-index: 101;
  transition: background ${(props) => (props.theme.animateTransition ? '200ms' : '0ms')} ease-in-out;

  ${(props) =>
    props.$border &&
    css`
      border-bottom: ${(props) => props.theme.borderThickness ?? '1px'} solid ${(props) => props.theme.foreground};
    `}
`

const LoggedInLinks = () => {
  const isTabletDown = useMedia(`(max-width: ${tabletMaxWidth}px)`)
  const { user } = useCurrentUser()
  const { authenticationService } = useAuthentication()
  const canShowLinks = useSelector(authenticationService, (state) => state.can('SHOW_MODAL'))

  const copyContext = useABTest('header-experiment-a', 'header-experiment-b')

  if (user) {
    return <UserProfile />
  }

  if (canShowLinks) {
    return (
      <SignupLinks>
        <SignInButton
          label="Log in"
          variant="ghost"
          size={isTabletDown ? 'small' : 'normal'}
          onClick={() => authenticationService.send({ type: 'SHOW_MODAL', mode: 'login', analyticsContext: 'header' })}
        />
        <SignUpButton
          label="Sign up"
          color="primary"
          size={isTabletDown ? 'small' : 'normal'}
          onClick={() => authenticationService.send({ type: 'SHOW_MODAL', mode: 'register', analyticsContext: 'header', copyContext })}
        />
      </SignupLinks>
    )
  }

  return null
}

interface Props {
  border?: boolean
  theme?: HeaderTheme
  motionStyle?: MotionStyle
}

export const Header = ({ border = true, theme = defaultTheme, motionStyle: style }: Props) => {
  const { menuOpen, setMenuOpen } = useUiContext()
  const { pathname } = useRouter()
  const { isLoggedIn } = useCurrentUser()
  const logoLinkDestination = pathname.startsWith('/admin') ? '/admin' : '/'

  return (
    <Container
      style={style}
      theme={theme}
      $border={border}
      initial={false}
      animate={menuOpen ? 'menuOpen' : 'menuClosed'}
      data-hide-fancy-cursor="true">
      <HeaderContent>
        <NextLink href={logoLinkDestination} passHref prefetch={false}>
          <LogoAnchor aria-label="Brands" onClick={() => setMenuOpen(false)}>
            <Logo theme={theme} menuOpen={menuOpen} />
          </LogoAnchor>
        </NextLink>

        <HeaderLinks>
          <PrimaryLinks $isSignedIn={isLoggedIn}>
            <PrimaryLink prefetch={false} href="/brands">
              Directory
            </PrimaryLink>
            <PrimaryLink prefetch={false} href="/stories">
              Stories
            </PrimaryLink>
            <PrimaryLink prefetch={false} href="/thingdrop">
              Thingdrop
            </PrimaryLink>
          </PrimaryLinks>

          <LoggedInLinks />

          <AlgoliaSearchProvider>
            <GlobalSearch />
          </AlgoliaSearchProvider>

          <Hamburger open={menuOpen} onClick={() => setMenuOpen(!menuOpen)} headerTheme={theme} />
          <Nav menuOpen={menuOpen} />
        </HeaderLinks>
      </HeaderContent>
    </Container>
  )
}
